import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createSessionApi, updateSessionApi } from '../services/sessionsService';
import { SessionResponse } from '../types/sessions';
import { getUserId, isStaff, isAdmin, isCoach } from '../utils/auth';
import ComboBox from './ComboBox';
import { useStaffMembers } from '../hooks/useStaffMembers';
import { autoFormatDateInput, parseDateOfBirthInputToIso, formatDateOfBirthUs } from '../utils/dateDisplay';
import '../styles/sessions-page.css';

interface LogSessionModalProps {
  athleteId: number;
  session?: SessionResponse | null;
  staffDisplayName: string;
  onClose: () => void;
}

const LogSessionModal: React.FC<LogSessionModalProps> = ({ athleteId, session, staffDisplayName, onClose }) => {
  const canEditSessionLead = isStaff() || isAdmin() || isCoach();
  const { staffMembers, loading: staffLoading } = useStaffMembers();
  const [selectedStaffId, setSelectedStaffId] = useState<number | undefined>(undefined);
  const [formData, setFormData] = useState({
    staffName: '',
    date: '',
    hour: '',
    minute: '',
    recordingLink: '',
    notesLink: '',
    notes: '',
  });
  const [dateText, setDateText] = useState<string>('');
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Prepopulate staff name and id for new sessions (logged-in user)
  useEffect(() => {
    if (!session && staffDisplayName) {
      setFormData(prev => ({ ...prev, staffName: staffDisplayName }));
      const loggedInIdStr = getUserId();
      if (loggedInIdStr) {
        const loggedInId = parseInt(loggedInIdStr, 10);
        if (!isNaN(loggedInId) && loggedInId > 0) {
          setSelectedStaffId(loggedInId);
        }
      }
    }
  }, [session, staffDisplayName]);

  useEffect(() => {
    if (session) {
      // Parse hours string into hour and minute if needed
      let hour = '';
      let minute = '';
      if (session.hours) {
        const hourMatch = session.hours.match(/(\d+)\s*hour/i);
        const minMatch = session.hours.match(/(\d+)\s*min/i);
        hour = hourMatch ? hourMatch[1].padStart(2, '0') : '';
        minute = minMatch ? minMatch[1].padStart(2, '0') : '';
      }

      const isoDate = session.date.split('T')[0];
      setFormData({
        staffName: session.staffName,
        date: isoDate,
        hour,
        minute,
        recordingLink: session.recordingLink || '',
        notesLink: session.notesLink || '',
        notes: session.notes || '',
      });
      setDateText(formatDateOfBirthUs(isoDate) ?? '');

      setSelectedStaffId(session.staffId);
    }
  }, [session]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStaffChange = (id: number | undefined) => {
    setSelectedStaffId(id);
    const selected = staffMembers.find(m => m.id === id);
    setFormData(prev => ({ ...prev, staffName: selected?.name ?? '' }));
    if (errors.staffName) {
      setErrors(prev => ({ ...prev, staffName: '' }));
    }
  };

  const handleTimeChange = (field: 'hour' | 'minute', value: string) => {
    // Allow empty string
    if (value === '') {
      setFormData(prev => ({ ...prev, [field]: '' }));
      if (errors.time) {
        setErrors(prev => ({ ...prev, time: '' }));
      }
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(value)) return;

    let numValue = parseInt(value, 10);

    // Validate ranges
    if (field === 'hour' && numValue > 23) numValue = 23;
    if (field === 'minute' && numValue > 59) numValue = 59;

    // Store the value without padding during typing
    setFormData(prev => ({ ...prev, [field]: numValue.toString() }));
    if (errors.time) {
      setErrors(prev => ({ ...prev, time: '' }));
    }
  };

  const handleTimeBlur = (field: 'hour' | 'minute') => {
    // Pad with zero on blur if there's a value
    const value = formData[field];
    if (value && value.length === 1) {
      setFormData(prev => ({ ...prev, [field]: value.padStart(2, '0') }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      setFiles(Array.from(e.dataTransfer.files));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (canEditSessionLead) {
      if (!selectedStaffId) {
        newErrors.staffName = 'Session lead is required';
      }
    } else {
      if (!formData.staffName.trim()) {
        newErrors.staffName = 'Staff name is required';
      }
    }

    const trimmedDate = dateText.trim();
    if (!trimmedDate) {
      newErrors.date = 'Date is required';
    } else if (!parseDateOfBirthInputToIso(trimmedDate)) {
      newErrors.date = 'Enter date as MM/DD/YYYY.';
    }

    // Check if hour is provided - minute can default to "00"
    const hasHour = formData.hour !== null && formData.hour !== undefined && formData.hour.trim() !== '';

    if (!hasHour) {
      newErrors.time = 'Time is required';
    }

    if (formData.recordingLink && !isValidUrl(formData.recordingLink)) {
      newErrors.recordingLink = 'Please enter a valid URL';
    }

    if (formData.notesLink && !isValidUrl(formData.notesLink)) {
      newErrors.notesLink = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty is valid since it's optional

    const trimmedUrl = url.trim();

    // If URL already has a protocol, validate it
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      try {
        new URL(trimmedUrl);
        return true;
      } catch {
        return false;
      }
    }

    // If no protocol, check basic URL structure:
    // - Should contain at least one dot (domain.extension)
    // - Should not contain spaces
    // - Should have some characters before and after the dot
    const urlPattern = /^[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+([\/\w\-._~:?#[\]@!$&'()*+,;=]*)?$/;
    return urlPattern.test(trimmedUrl);
  };

  const normalizeUrl = (url: string): string => {
    if (!url || !url.trim()) return '';

    const trimmedUrl = url.trim();

    // If URL already has a protocol, return as is
    if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
      return trimmedUrl;
    }

    // Otherwise, add https:// prefix
    return `https://${trimmedUrl}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Resolve staffId: use selected staff member id (for staff/admin) or fall back to logged-in user
      let staffId: number;
      if (canEditSessionLead && selectedStaffId) {
        staffId = selectedStaffId;
      } else {
        const staffIdStr = getUserId();
        if (!staffIdStr || staffIdStr === '0') {
          throw new Error('Unable to determine staff ID. Please log in again.');
        }
        staffId = parseInt(staffIdStr, 10);
        if (isNaN(staffId) || staffId <= 0) {
          throw new Error('Invalid staff ID. Please log in again.');
        }
      }

      // Format hours string from hour and minute (default minute to 0 if empty)
      const hourNum = parseInt(formData.hour, 10);
      const minuteNum = formData.minute ? parseInt(formData.minute, 10) : 0;
      let hoursStr = '';
      if (hourNum > 0) hoursStr += `${hourNum} hour${hourNum > 1 ? 's' : ''}`;
      if (minuteNum > 0) {
        if (hoursStr) hoursStr += ', ';
        hoursStr += `${minuteNum} min${minuteNum > 1 ? 's' : ''}`;
      }

      const resolvedDateIso = formData.date || parseDateOfBirthInputToIso(dateText.trim()) || '';

      const sessionData = {
        athleteId,
        staffId,
        staffName: formData.staffName,
        date: resolvedDateIso,
        hours: hoursStr,
        recordingLink: formData.recordingLink ? normalizeUrl(formData.recordingLink) : undefined,
        notesLink: formData.notesLink ? normalizeUrl(formData.notesLink) : undefined,
        notes: formData.notes || undefined,
      };

      if (session) {
        await updateSessionApi(athleteId, session.id, sessionData);
      } else {
        await createSessionApi(athleteId, sessionData);
      }

      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save session');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="session-modal-overlay">
      <div className="session-modal">
        <div className="session-modal-header">
          <h2 className="session-modal-title">
            {session ? 'Edit session' : 'Log session'}
          </h2>
          <button
            className="session-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            data-testid="close-modal-button"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="session-modal-content">
            {saveError && (
              <div className="sessions-error" data-testid="save-error">
                {saveError}
              </div>
            )}

            <div className="session-form">
              {/* Session Lead */}
              <div className="session-form-group">
                <label htmlFor="staffName" className="session-form-label-small">
                  Session lead
                </label>
                {canEditSessionLead ? (
                  <div className="session-staff-combobox">
                    <ComboBox
                      value={selectedStaffId}
                      options={staffMembers}
                      onChange={handleStaffChange}
                      loading={staffLoading}
                      placeholder="Search for a staff member..."
                      noOptionsMessage="No staff or admin users found"
                      testId="staff-name-input"
                      error={errors.staffName}
                    />
                  </div>
                ) : (
                  <input
                    type="text"
                    id="staffName"
                    name="staffName"
                    value={formData.staffName}
                    onChange={handleChange}
                    className="session-form-input session-form-input-readonly"
                    placeholder="Enter name of staff"
                    data-testid="staff-name-input"
                    readOnly
                  />
                )}
                {!canEditSessionLead && errors.staffName && (
                  <span className="session-form-error">{errors.staffName}</span>
                )}
              </div>

              {/* Date and Time Section */}
              <div className="session-datetime-section">
                <h3 className="session-section-title">Enter date and time of session</h3>
                <div className="session-datetime-grid">
                  <div className="session-form-group">
                    <label htmlFor="date" className="session-form-label-caps">
                      ENTER DATE
                    </label>
                    <input
                      type="text"
                      id="date"
                      name="date"
                      inputMode="numeric"
                      placeholder="MM/DD/YYYY"
                      value={dateText}
                      onChange={(e) => {
                        setDateText(autoFormatDateInput(e.target.value, dateText));
                        if (errors.date) {
                          setErrors(prev => ({ ...prev, date: '' }));
                        }
                      }}
                      onBlur={() => {
                        const trimmed = dateText.trim();
                        if (!trimmed) {
                          setFormData(prev => ({ ...prev, date: '' }));
                          return;
                        }
                        const iso = parseDateOfBirthInputToIso(trimmed);
                        if (iso) {
                          setFormData(prev => ({ ...prev, date: iso }));
                          setDateText(formatDateOfBirthUs(iso) ?? trimmed);
                        } else {
                          setFormData(prev => ({ ...prev, date: '' }));
                        }
                      }}
                      className={`session-form-input ${errors.date ? 'input-error' : ''}`}
                      data-testid="date-input"
                    />
                    {errors.date && (
                      <span className="session-form-error">{errors.date}</span>
                    )}
                  </div>

                  <div className="session-form-group">
                    <label className="session-form-label-caps">
                      ENTER TIME
                    </label>
                    <div className="session-time-inputs">
                      <div className="session-time-field">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formData.hour}
                          onChange={(e) => handleTimeChange('hour', e.target.value)}
                          onBlur={() => handleTimeBlur('hour')}
                          className="session-time-input"
                          placeholder="00"
                          maxLength={2}
                          autoComplete="off"
                          data-testid="hour-input"
                        />
                        <span className="session-time-label">Hour</span>
                      </div>
                      <span className="session-time-separator">:</span>
                      <div className="session-time-field">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={formData.minute}
                          onChange={(e) => handleTimeChange('minute', e.target.value)}
                          onBlur={() => handleTimeBlur('minute')}
                          className="session-time-input"
                          placeholder="00"
                          maxLength={2}
                          autoComplete="off"
                          data-testid="minute-input"
                        />
                        <span className="session-time-label">Minute</span>
                      </div>
                    </div>
                    {errors.time && (
                      <span className="session-form-error">{errors.time}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Session Links */}
              <div className="session-links-section">
                <h3 className="session-section-title">Add session links</h3>
                <div className="session-links-grid">
                  <div className="session-form-group">
                    <label htmlFor="recordingLink" className="session-form-label-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.5 3.5L3.5 7.5L7.5 11.5M8.5 3.5L12.5 7.5L8.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      RECORDING
                    </label>
                    <input
                      type="text"
                      id="recordingLink"
                      name="recordingLink"
                      value={formData.recordingLink}
                      onChange={handleChange}
                      className={`session-form-input ${errors.recordingLink ? 'error' : ''}`}
                      placeholder="Enter google meet recording link"
                      data-testid="recording-link-input"
                    />
                    {errors.recordingLink && (
                      <span className="session-form-error">{errors.recordingLink}</span>
                    )}
                  </div>

                  <div className="session-form-group">
                    <label htmlFor="notesLink" className="session-form-label-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.5 3.5L3.5 7.5L7.5 11.5M8.5 3.5L12.5 7.5L8.5 11.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      NOTES
                    </label>
                    <input
                      type="text"
                      id="notesLink"
                      name="notesLink"
                      value={formData.notesLink}
                      onChange={handleChange}
                      className={`session-form-input ${errors.notesLink ? 'error' : ''}`}
                      placeholder="Enter gemini notes link"
                      data-testid="notes-link-input"
                    />
                    {errors.notesLink && (
                      <span className="session-form-error">{errors.notesLink}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Session Notes */}
              <div className="session-form-group">
                <label htmlFor="notes" className="session-form-label-caps">
                  ADD SESSION NOTES
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="session-form-textarea"
                  placeholder="Short summary of session"
                  data-testid="notes-textarea"
                />
              </div>
            </div>
          </div>

          <div className="session-modal-footer">
            <button
              type="submit"
              className="session-modal-button primary"
              disabled={saving}
              data-testid="save-button"
            >
              {saving ? 'Saving...' : session ? 'Update session' : 'Log session'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default LogSessionModal;

